import styled from "styled-components";
import { colors } from "../../../libs/themes";

interface MediaCardWrapperProps {
    customBorderRadius?: string;
    isSelected?: boolean;
}

export const MediaPoster = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !["applyResponsive", "autoWidth"].includes(prop),
})<{ applyResponsive?: boolean; autoWidth?: boolean }>`
    position: relative;
    width: ${({ autoWidth }) => (autoWidth ? "256px" : "256px")};

    ${({ applyResponsive }) =>
        applyResponsive &&
        `
    @media (max-width: 900px) {
      width: 160px;
    }

    @media (max-width: 767px) {
      width: 256px;
    }
  `}

    /* ✅ Auto width for 1700px to 2600px wide devices */
  @media (min-width: 1700px) and (max-width: 3350px) {
        ${({ autoWidth }) =>
            autoWidth &&
            `
      width: auto;
    `}
    }

    &.hover-intent:not(.is-mobile) {
        & .media-poster--link {
            & .watch-label {
                visibility: visible;
                opacity: 1;
                transform: translateX(-50%) scale(1);
                transition:
                    opacity 0.3s ease-out 0.15s,
                    transform 0.3s var(--ease-out-back) 0.15s;
            }
        }

        & .media-poster--art {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            transition: all 0.3s ease-out;

            & img {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
                box-shadow: 0px -5px 15px -3px
                    var(--video-card-poster-shadow-color);
                transition: all 0.3s ease-out;
            }
        }
    }
`;

export const MediaPosterLink = styled.a`
    line-height: 0;
    text-decoration: none;
    width: 100%;
    height: 100%;
    padding: 12px 24px;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 999;

    @media (hover: hover) {
        &:hover {
            cursor: pointer;
            transition: transform 0.3s ease;
        }
    }

    &:focus {
        outline: none;
    }
`;

export const MediaPosterArt = styled.div`
    line-height: 0;
    aspect-ratio: 16 / 9;
    background-color: ${({ theme }) => theme.cardSeparator};
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.2);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: var(--video-card-border-radius);
    border-top-right-radius: var(--video-card-border-radius);

    @media (min-width: 576px) {
    }
    @media (min-width: 768px) {
        &:not(.is-mobile) {
            border-bottom-left-radius: var(--video-card-border-radius);
            border-bottom-right-radius: var(--video-card-border-radius);
        }
    }
    @media (min-width: 992px) {
    }
    @media (min-width: 1200px) {
    }
    @media (min-width: 1400px) {
    }
`;

export const MediaPosterImage = styled.img`
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    object-position: 50% 50%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: var(--video-card-border-radius);
    border-top-right-radius: var(--video-card-border-radius);
    transition: all 0.3s ease-out;

    @media (min-width: 576px) {
    }
    @media (min-width: 768px) {
        &:not(.is-mobile) {
            border-bottom-left-radius: var(--video-card-border-radius);
            border-bottom-right-radius: var(--video-card-border-radius);
        }
    }
`;

export const MediaAncillary = styled.article`
    background-color: ${({ theme }) => theme.cardBg};
    width: 100%;
    max-height: none;
    padding: 0 20px 18px 20px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    box-shadow: 0px 10px 15px -3px var(--video-card-shadow-color);
    margin-top: -1px;
    opacity: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 auto;
    position: relative;
    top: auto;
    transition: all 0.3s ease-out;
    will-change: max-height, opacity;
    display: none;

    @media (min-width: 576px) {
    }
    @media (min-width: 768px) {
        &:not(.is-mobile) {
            max-height: 0;
            opacity: 0;
            flex: 0 1 auto;
            position: absolute;
            top: 100%;
        }
    }
    @media (min-width: 992px) {
    }
    @media (min-width: 1200px) {
    }
    @media (min-width: 1400px) {
    }

    &.hover-intent:not(.is-mobile) {
        max-height: 420px;
        padding: 0 20px 20px 20px;
        opacity: 1;
        transition: all 0.3s ease-out;
    }

    & section {
        margin-top: 12px;
    }
`;

export const MediaMetadata = styled.section`
    line-height: normal;
`;

export const MediaHighlight = styled.span`
    font-size: 13.01px;
    font-weight: 700;
    line-height: 1.337;
    text-transform: initial;
    color: ${({ theme }) => theme.cardTextAlt};
    margin-top: 8px;
    display: none;
`;

export const MediaSynopsis = styled.div`
    font-size: 13.01px;
    font-weight: 400;
    line-height: 1.337;
    text-transform: initial;
    color: ${({ theme }) => theme.cardTextAlt};
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    max-height: calc(var(21.39px) * 3);
    margin-top: 8px;
    overflow: hidden;
    display: -webkit-box;
`;

export const MediaGenre = styled.div`
    font-size: 13.01px;
    font-weight: 400;
    line-height: 1.337;
    text-transform: initial;
    color: ${({ theme }) => theme.cardTextAlt};
    margin-top: 8px;

    & > span {
        margin-right: 12px;
        position: relative;

        &::after {
            content: "◦";
            font-size: 14px;
            font-weight: 400;
            line-height: 1.337;
            color: var(--video-card-separator-color);
            width: 12px;
            height: 12px;
            position: absolute;
            top: 50%;
            right: -15.01px;
            transform: translateY(calc(-50% + 1.01px));
        }

        &:last-of-type {
            margin-right: 0;

            &::after {
                display: none;
            }
        }
    }
`;

export const MediaCrew = styled.div`
    margin-top: 8px;
`;

export const CrewItem = styled.div`
    font-size: 13.01px;
    font-weight: 400;
    line-height: 1.337;
    text-transform: initial;
    color: ${({ theme }) => theme.cardTextAlt};
    margin-top: 0;
    margin-bottom: 8px;

    &:last-of-type {
        margin-bottom: 0;
    }

    & > span {
        font-weight: 700;
        display: block;
    }
`;

export const MediaRating = styled.div`
    margin: 8px 0 4px 0;
    display: flex;
    flex-flow: row;
    justify-content: flex-start;
    align-items: center;
`;

export const MediaCard = styled.article`
    position: relative;
    z-index: 5;

    &.hover-intent:not(.is-mobile) {
        z-index: 15;

        & .media-card--wrapper {
            transform: scale(1.063);
            transition: all 0.3s ease-out;
        }
    }
`;

export const MediaCardWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !["isSelected", "customBorderRadius"].includes(prop),
})<MediaCardWrapperProps>`
    position: relative;
    width: 100%;
    border: ${({ isSelected, theme }) =>
        isSelected ? `2px solid ${theme.borderContrast}` : `1px solid ${theme.cardSeparator}`};
    border-radius: 8px;
    overflow: hidden;
    transition: box-shadow 0.3s ease;

    &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${colors.background.transparentBlack};
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
        z-index: 9;
    }

    &:hover {
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        transition:
            box-shadow 0.3s ease,
            transform 0.3s ease;

        &::after {
            opacity: 1;
        }
    }
`;
